import { useCallback, useMemo, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Plus, Download, Upload, Mail } from 'lucide-react';
import { DataTable } from '~/components/ui/data-table';
import { Button } from '~/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import type { AppLayoutContext } from '~/types';
import { createFamilyColumns } from './columns';
import { AddFamilyDialog } from './AddFamilyDialog';
import ImportFamiliesDialog from './ImportFamiliesDialog';
import { useFamiliesListQuery } from './useFamiliesListQuery';
import { useExportFamilies } from './useExportFamilies';
import { useDeleteFamily } from './useDeleteFamily';
import { useInviteAllGuardians } from '~/hooks/useBulkAccountInvites';

interface DeleteDialogState {
  open: boolean;
  family: any | null;
}

function FamiliesList() {
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const navigate = useNavigate();
  const [isAddFamilyOpen, setIsAddFamilyOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const selectedSchoolYear = roleContext?.selectedSchoolYear;

  const { data, loading, error, refetch } = useFamiliesListQuery(school?.id);

  const [exportFamilies] = useExportFamilies();
  const [deleteFamily] = useDeleteFamily();
  const [inviteAllGuardians, { loading: invitingGuardians }] = useInviteAllGuardians();
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, family: null });

  const families = data?.school?.families || [];

  const handleRowClick = (family: any) => {
    navigate(`/app/${school?.slug}/families/${family.slug}`);
  };

  const handleViewFamily = useCallback((family: any) => {
    navigate(`/app/${school?.slug}/families/${family.slug}`);
  }, [navigate, school?.slug]);

  const handleDeleteFamily = async (family: any) => {
    try {
      const { data } = await deleteFamily({
        variables: {
          input: {
            familyId: family.id,
          },
        },
      });

      if (data?.deleteFamily?.userErrors?.length && data.deleteFamily.userErrors.length > 0) {
        alert('Error deleting family: ' + data.deleteFamily.userErrors.map((e: any) => e.message).join(', '));
        return;
      }

      if (data?.deleteFamily?.success) {
        refetch();
        setDeleteDialog({ open: false, family: null });
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      alert('Error deleting family: ' + err.message);
    }
  };

  const columns = useMemo(
    () => createFamilyColumns({
      onViewFamily: handleViewFamily,
      onDeleteFamily: (family) => setDeleteDialog({ open: true, family }),
      hasEnrollments: () => false, // We'll let the backend check this
    }),
    [handleViewFamily]
  );

  const handleExport = async (selectedFamilyIds: string[] | null = null) => {
    setIsExporting(true);
    try {
      const { data: exportData } = await exportFamilies({
        variables: {
          input: {
            schoolId: school?.id,
            familyIds: selectedFamilyIds && selectedFamilyIds.length > 0 ? selectedFamilyIds : undefined,
          },
        },
      });

      if (exportData?.exportFamilies?.userErrors?.length && exportData.exportFamilies.userErrors.length > 0) {
        console.error('Export errors:', exportData.exportFamilies.userErrors);
        alert('Error exporting families: ' + exportData.exportFamilies.userErrors.map((e: any) => e.message).join(', '));
        return;
      }

      // Download Family TSV
      const familyBlob = new Blob([exportData?.exportFamilies?.familyTsv ?? ''], { type: 'text/tab-separated-values' });
      const familyUrl = URL.createObjectURL(familyBlob);
      const familyLink = document.createElement('a');
      familyLink.href = familyUrl;
      familyLink.download = `families-export-${new Date().toISOString().split('T')[0]}.tsv`;
      document.body.appendChild(familyLink);
      familyLink.click();
      document.body.removeChild(familyLink);
      URL.revokeObjectURL(familyUrl);

      // Download Children TSV
      const childrenBlob = new Blob([exportData?.exportFamilies?.childrenTsv ?? ''], { type: 'text/tab-separated-values' });
      const childrenUrl = URL.createObjectURL(childrenBlob);
      const childrenLink = document.createElement('a');
      childrenLink.href = childrenUrl;
      childrenLink.download = `children-export-${new Date().toISOString().split('T')[0]}.tsv`;
      document.body.appendChild(childrenLink);
      childrenLink.click();
      document.body.removeChild(childrenLink);
      URL.revokeObjectURL(childrenUrl);
    } catch (err: any) {
      console.error('Export error:', err);
      alert('Error exporting families: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleInviteAllGuardians = async () => {
    if (!school?.id || !selectedSchoolYear?.id) return;
    if (!window.confirm(`Invite every eligible guardian for ${selectedSchoolYear.name}? Guardians with accounts or pending invitations will be skipped.`)) return;

    const result = await inviteAllGuardians({
      variables: { input: { schoolId: school.id, schoolYearId: selectedSchoolYear.id } },
    });
    const payload = result.data?.inviteAllGuardians;
    if (payload?.userErrors?.length) {
      alert(payload.userErrors.map((error) => error.message).join('\n'));
      return;
    }

    const skipped = payload?.results
      .filter((item) => item.status === 'SKIPPED')
      .map((item) => `${item.person.fullName}: ${item.reason}`) ?? [];
    alert([
      `Queued ${payload?.sentCount ?? 0} invitation(s); skipped ${payload?.skippedCount ?? 0}.`,
      ...(skipped.length ? ['', ...skipped] : []),
    ].join('\n'));
  };

  const bulkActions = [
    {
      label: 'Export Selected',
      onClick: (selectedRows: any[]) => {
        const selectedFamilyIds = selectedRows.map(row => row.id);
        handleExport(selectedFamilyIds);
      },
      variant: 'outline' as const,
    },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-muted rounded mb-6"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-destructive">
          Error loading families: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Families</h1>
          <p className="text-muted-foreground">
            Manage families at {data?.school?.name || school?.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleInviteAllGuardians}
            disabled={invitingGuardians || !selectedSchoolYear}
            title={selectedSchoolYear ? `Invite approved-family guardians for ${selectedSchoolYear.name}` : 'Select a school year first'}
          >
            <Mail className="h-4 w-4 mr-2" />
            {invitingGuardians ? 'Inviting...' : 'Invite Eligible Guardians'}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport(null)}
            disabled={isExporting || loading}
          >
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export All'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsImportOpen(true)}
            disabled={loading}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={() => setIsAddFamilyOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Family
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={families}
        onRowClick={handleRowClick}
        searchColumn="name"
        searchPlaceholder="Search families..."
        defaultSorting={[{ id: 'name', desc: false }]}
        stateKey="families-list"
        enableRowSelection={true}
        bulkActions={bulkActions}
      />

      <AddFamilyDialog
        open={isAddFamilyOpen}
        onOpenChange={setIsAddFamilyOpen}
        schoolId={school?.id}
      />

      <ImportFamiliesDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        schoolId={school?.id}
        onSuccess={() => {
          refetch();
        }}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, family: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Family</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.family?.name}</strong>?
              This will permanently delete the family and all its members. This action cannot be undone.
              <br /><br />
              <strong>Note:</strong> Families with enrollments cannot be deleted. Delete enrollments first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteFamily(deleteDialog.family)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Family
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default FamiliesList;
